import { Component } from 'react';
import { Cell } from '@table-library/react-table-library/table';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { MODE } from '../../PagesQueriesChanges/scope';
import { operatorLabel } from '../groups';
import { comparisonTableColumns } from '../keys';
import { MODES } from '../scope';

/**
 * Block 02 — every group the session defined, each against its own baseline.
 *
 * Each row is its own page-filtered, page-aggregated response, which is why rows fail separately:
 * one group erroring leaves the others' figures on screen. The table never gains a total row.
 * Groups can overlap — a page that meets two groups' rules is in both rows — so a sum across them
 * would count some pages twice and read as the site's content traffic, which it is not.
 *
 * The modes re-order the rows already held rather than re-asking, the same three orders the rows
 * of Pages, Queries & Changes use. Growing and Declining keep only groups that returned both
 * windows: a group with no baseline row has no movement, and ordering it as if its change were
 * zero would place it among groups that genuinely did not move.
 *
 * Picking a group is what blocks 03 and 04 are about. The pick is stated three times over — the
 * row's button reads Showing, the row stays marked, and the chip beside the modes names the group
 * with its rules and offers the way back out. Clearing it drops whatever the detail blocks were
 * still waiting for and leaves them waiting for the next pick, rather than for a whole-property
 * answer nobody asked for.
 */
class GroupComparison extends Component {
    constructor(props) {
        super(props);
        this.additionalBox = this.additionalBox.bind(this);
        this.actionColumn = this.actionColumn.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
    }

    /** Clicking the row does what its button does, including toggling back off. */
    handleRowClick(item) {
        const { activeGroup, onSelect } = this.props;
        onSelect(item.key === activeGroup ? null : item.key);
    }

    /**
     * The last column: the button that loads a group's trend and matching URLs into the blocks
     * below. Left out of the exports — a control is not a value.
     */
    actionColumn() {
        const { t, activeGroup, onSelect } = this.props;

        return {
            label: t('tools.content_groups.comparison.details'),
            key: 'details',
            data: 'key',
            sortType: (array) => array,
            cellSize: 'minmax(140px, 160px)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (csvDownload) return '';

                const picked = item.key === activeGroup;

                return (
                    <div className="d-flex">
                        <button
                            type="button"
                            className={`table-action-main-btn ${picked ? 'is-picked' : ''}`}
                            aria-pressed={picked}
                            onClick={() => onSelect(picked ? null : item.key)}
                        >
                            {picked && <i className="icon-check-icon" aria-hidden="true" />}
                            {picked
                                ? t('tools.content_groups.comparison.showing')
                                : t('tools.content_groups.comparison.show')}
                        </button>
                    </div>
                );
            },
        };
    }

    /**
     * Popular / Growing / Declining, drawn as the table-filter control the Categories table uses.
     * The metric the order reads and the active group sit beside the group of buttons rather than
     * inside it: neither is one of the three orders.
     */
    additionalBox() {
        const { t, scope, activeGroup, onMode, onSelect } = this.props;
        const group = scope.groups.find((item) => item.name === activeGroup);
        const chip = group
            ? t('tools.content_groups.comparison.active', {
                  group: group.name,
                  rules: group.rules
                      .map((rule) => `${operatorLabel(t, rule.operator)} ${rule.value}`)
                      .join(' AND '),
              })
            : null;

        return (
            <div className="table-additional-view">
                <div className="table-additional-view-btns">
                    <div className="table-additional-view-btn-groups">
                        {MODES.map((mode) => (
                            <button
                                type="button"
                                key={mode}
                                className={scope.mode === mode ? 'active' : ''}
                                aria-pressed={scope.mode === mode}
                                onClick={() => onMode(mode)}
                            >
                                {t(`tools.content_groups.modes.${mode}`)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="gsc-tool-rows-meta">
                    <span className="gsc-tool-sub">
                        {t('tools.content_groups.comparison.ordered_by', {
                            metric: t(`tools.gsc.metrics.${scope.metric}_short`),
                        })}
                    </span>
                    {!!group && (
                        <span className="gsc-tool-chip" title={chip}>
                            <span>{chip}</span>
                            <button
                                type="button"
                                aria-label={t('tools.content_groups.comparison.clear', {
                                    group: group.name,
                                })}
                                onClick={() => onSelect(null)}
                            >
                                <i className="icon-x-close" />
                            </button>
                        </span>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { t, scope, totals, rows, activeGroup, onExport } = this.props;

        const columns = comparisonTableColumns(t, {
            hasPosition: reportsPosition(scope.searchType),
        });
        const tableColumns = [...columns, this.actionColumn()];

        const loading = scope.groups.some((group) =>
            ['idle', 'loading'].includes(totals[group.name]?.status),
        );
        const failed = scope.groups.filter((group) => totals[group.name]?.status === 'error');
        const noComparison = !scope.baseline;

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">
                            02 · {t('tools.content_groups.comparison.title')}
                        </p>
                        <p className="gsc-tool-sub">
                            {t('tools.content_groups.comparison.description')} ·{' '}
                            {formatPeriod(scope.current)}
                            {!noComparison && <> ↔ {formatPeriod(scope.baseline)}</>}
                        </p>
                        <p className="gsc-tool-rows-hint">
                            <i className="icon-info" aria-hidden="true" />
                            {t('tools.content_groups.comparison.select_hint')}
                        </p>
                    </div>

                    <div className="gsc-tool-icon-actions">
                        <button
                            type="button"
                            title={t('tools.content_groups.comparison.export')}
                            aria-label={t('tools.content_groups.comparison.export')}
                            onClick={onExport}
                            disabled={loading || !rows.length}
                        >
                            <i className="icon-fi_download" />
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    {noComparison && scope.mode !== MODE.POPULAR && (
                        <div className="gsc-tool-banner">
                            {t('tools.content_groups.comparison.no_comparison')}
                        </div>
                    )}

                    {loading && <Loading padding={20} />}

                    {/* One banner per failed group, naming it: "the request failed" is not an
                        answer when the rest of the table is still showing numbers. */}
                    {!loading &&
                        failed.map((group) => (
                            <div key={group.name} className="gsc-tool-banner error">
                                {t('tools.content_groups.comparison.group_failed', {
                                    group: group.name,
                                    reason:
                                        totals[group.name]?.error ??
                                        t('global.something_went_wrong'),
                                })}
                            </div>
                        ))}

                    {!loading && failed.length < scope.groups.length && (
                        <>
                            <DataTable
                                nodes={rows}
                                COLUMNS={tableColumns}
                                customColumnsForExport={columns}
                                sortFns={getSortKeys(tableColumns)}
                                additionalStyles={setAdditionalStyles(tableColumns)}
                                additionalBox={this.additionalBox}
                                fileName={t('tools.content_groups.comparison.title')}
                                customEmptyMessage={t(
                                    `tools.content_groups.comparison.empty.${scope.mode}`,
                                )}
                                customContainerStyles="gsc-tool-key-table"
                                rowClassName={(item) =>
                                    item.key === activeGroup ? 'dt-row-picked' : ''
                                }
                                onRowClick={this.handleRowClick}
                            />

                            <p className="gsc-tool-table-note">
                                {t('tools.content_groups.comparison.no_total')}
                            </p>
                            <p className="gsc-tool-table-note">
                                {t('tools.content_groups.comparison.aggregation_note')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(GroupComparison);
